import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Plus, Edit, Trash2, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPantries() {
  const [pantries, setPantries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPantry, setEditingPantry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    schedule: '',
    phone: '',
    contact_email: '',
    maps_url: '',
    special_notes: '',
    is_active: true
  });

  useEffect(() => {
    fetchPantries();
  }, []);

  const fetchPantries = async () => {
    try {
      const response = await axios.get(`${API}/pantries`);
      setPantries(response.data);
    } catch (error) {
      toast.error('Failed to load pantries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPantry) {
        await axios.put(`${API}/pantries/${editingPantry.id}`, formData);
        toast.success('Pantry updated');
      } else {
        await axios.post(`${API}/pantries`, formData);
        toast.success('Pantry created');
      }
      setShowDialog(false);
      resetForm();
      fetchPantries();
    } catch (error) {
      toast.error('Failed to save pantry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pantry location?')) return;
    try {
      await axios.delete(`${API}/pantries/${id}`);
      toast.success('Pantry deleted');
      fetchPantries();
    } catch (error) {
      toast.error('Failed to delete pantry');
    }
  };

  const openEdit = (pantry) => {
    setEditingPantry(pantry);
    setFormData({
      name: pantry.name || '',
      address: pantry.address || '',
      schedule: pantry.schedule || '',
      phone: pantry.phone || '',
      contact_email: pantry.contact_email || '',
      maps_url: pantry.maps_url || '',
      special_notes: pantry.special_notes || '',
      is_active: pantry.is_active !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingPantry(null);
    setFormData({
      name: '',
      address: '',
      schedule: '',
      phone: '',
      contact_email: '',
      maps_url: '',
      special_notes: '',
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-pantries-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Food Pantries</h1>
          <p className="text-charcoal/60">Manage pantry locations</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-pantry-btn"
        >
          <Plus size={18} className="mr-2" /> Add Pantry
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {pantries.map((pantry) => (
          <motion.div
            key={pantry.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-olive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="text-olive" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-lg text-charcoal">{pantry.name}</h3>
                    {!pantry.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-charcoal/60 mt-1">{pantry.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(pantry)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(pantry.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              {pantry.schedule && (
                <div className="flex items-center gap-2 text-charcoal/60">
                  <Clock size={14} /> {pantry.schedule}
                </div>
              )}
              {pantry.phone && (
                <div className="flex items-center gap-2 text-charcoal/60">
                  <Phone size={14} /> {pantry.phone}
                </div>
              )}
              {pantry.contact_email && (
                <div className="flex items-center gap-2 text-charcoal/60">
                  <Mail size={14} /> {pantry.contact_email}
                </div>
              )}
              {pantry.maps_url && (
                <a href={pantry.maps_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-gold hover:underline">
                  <ExternalLink size={14} /> View on Google Maps
                </a>
              )}
            </div>
            
            {pantry.special_notes && (
              <div className="mt-4 pt-4 border-t border-gold/10">
                <p className="text-sm text-charcoal/70 italic">{pantry.special_notes}</p>
              </div>
            )}
          </motion.div>
        ))}
        {pantries.length === 0 && (
          <div className="col-span-2 text-center py-12 text-charcoal/50">
            <MapPin size={48} className="mx-auto mb-4 opacity-20" />
            <p>No pantry locations yet. Add your first location!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingPantry ? 'Edit Pantry' : 'Add Pantry Location'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Pantry name"
                required
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Full address"
                required
              />
            </div>
            <div>
              <Label>Schedule *</Label>
              <Input
                value={formData.schedule}
                onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                placeholder="e.g., Mon & Wed 9am-12pm"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Google Maps URL</Label>
              <Input
                value={formData.maps_url}
                onChange={(e) => setFormData({...formData, maps_url: e.target.value})}
                placeholder="https://maps.google.com/..."
              />
            </div>
            <div>
              <Label>Special Notes</Label>
              <Textarea
                value={formData.special_notes}
                onChange={(e) => setFormData({...formData, special_notes: e.target.value})}
                placeholder="e.g., Bring ID, First come first served"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingPantry ? 'Update Pantry' : 'Create Pantry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
